// =============================================================================
// icones.js — LE jeu d'icônes d'Aramis (lot L3 de la refonte « Mosaïque vivante »)
// =============================================================================
// Pourquoi : la navigation affichait 125 emoji (117 dans navConstants, 8 dans
// instancesNav), rendus différemment par chaque système (Apple, Segoe, Noto) et
// interdits par la charte Enio. Ici : un seul jeu de tracés au trait, repris de
// Tabler Icons (licence MIT, en-tête de src/iconesTraces.js), en SVG en ligne
// (aucune police, compatible CSP), currentColor, grille 24.
//
//   <Icone name="users" size={20} />          glyphe nu, couleur du texte
//   <Tuile couleur={NAV_COLORS.rh} taille={28} icone="users" variante="pleine" />
//     pleine : fond = la teinte, glyphe blanc ou navy profond (encreSur)
//     lavis  : fond = la teinte à 14 %, glyphe à l'encre du thème (navTexte)
//     nue    : sans fond, glyphe à l'encre du thème
//
// Les champs `icon` de navConstants.js et instancesNav.js sont des CLÉS de ce
// jeu : src/iconesNav.test.js fait échouer la CI sur un emoji ou une clé
// inconnue. Ajouter une icône : une ligne dans JEU (scripts/generer-icones.mjs),
// puis relancer le script. Ne jamais écrire un tracé à la main.
// Aucun hex ici (cliquet de L2) : la teinte navy vient de NAV_COLORS, sa
// variante de thème des jetons --tuile-navy* de theme.css.
// =============================================================================
import { createElement, useEffect, useState } from "react";
import { TRACES_TABLER } from "./iconesTraces";
import { NAV_COLORS, navTexte, encreSur } from "./navConstants";
import { alpha } from "./alpha";
import { taillePx } from "./echelleTexte";

// Glyphes propres à Enio, dérivés de la tuile (pas de Tabler).
const TRACES_ENIO = {
  // Losange plein : marque d'une INSTANCE (AG, CA, CODIR, Bureau). Synthèse
  // Mosaïque §4.5 : « une instance n'a pas d'icône, sa tuile porte un petit
  // losange plein de sa sous-teinte » — fin du temple identique pour AG et CA.
  losange: [["path", { d: "M12 4.5l7.5 7.5l-7.5 7.5l-7.5 -7.5z", fill: "currentColor" }]],
};

export const ICONES = Object.freeze({ ...TRACES_TABLER, ...TRACES_ENIO });
export const estIcone = (nom) => typeof nom === "string" && Object.prototype.hasOwnProperty.call(ICONES, nom);

// Trait : 2 px jusqu'à 16 px (sinon le glyphe s'efface), 1,75 px de 17 à 27 px
// (synthèse §4.5), 1,5 px à partir de 28 px.
const traitPour = (size) => (size <= 16 ? 2 : size >= 28 ? 1.5 : 1.75);

export function Icone({ name, size = 20, strokeWidth, titre, style, className, traces }) {
  const noeuds = traces || ICONES[name];
  if (!noeuds) return null;
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth={strokeWidth ?? traitPour(size)}
      strokeLinecap="round" strokeLinejoin="round" focusable="false"
      role={titre ? "img" : undefined} aria-label={titre || undefined}
      aria-hidden={titre ? undefined : "true"} className={className}
      style={{ flexShrink: 0, display: "inline-block", verticalAlign: "middle", ...style }}>
      {noeuds.map((n, i) => (typeof n === "string"
        ? createElement("path", { key: i, d: n })
        : createElement(n[0], { key: i, ...n[1] })))}
    </svg>
  );
}

// Transition : une clé → <Icone> ; un texte hérité (emoji d'un appelant pas
// encore migré, ex. prop `icon` d'un <ModuleHero>, lot L10) → le texte tel quel.
export function IconeOuTexte({ icone, size = 20, style }) {
  if (!icone) return null;
  if (estIcone(icone)) return <Icone name={icone} size={size} style={style} />;
  return <span aria-hidden="true" style={{ lineHeight: 1, ...style }}>{icone}</span>;
}

// ── Icône DANS une ligne de texte (lot L10d, ex-emoji des libellés) ─────────
// Elle suit la taille du texte (1,15 em), trait de 2 (lisible aux petites
// tailles), posée sur la ligne de base. couleur : encre de statut (✅ vert,
// ❌ rouge, ⚠ orange), sinon celle du texte. titre : nom accessible quand
// l'icône est tout le contenu d'un bouton.
// Jeu ÉTENDU : les glyphes hors du jeu de base vivent dans
// src/iconesTracesEtendus.js (GÉNÉRÉ, --etendu), un morceau PARESSEUX unique,
// chargé au premier besoin et préchargé au repos. Ne jamais l'importer
// statiquement : sous le seuil de découpe de webpack, il serait recopié dans
// chaque écran (+223 Ko gz mesurés). En attendant, une case vide de la même
// taille : rien ne saute.
let tracesEtendus = null, chargement = null;
const abonnesEtendus = new Set();
export function chargerIconesEtendues() {
  if (!chargement) {
    chargement = import("./iconesTracesEtendus").then((m) => {
      tracesEtendus = m.TRACES_ETENDUS;
      abonnesEtendus.forEach((f) => f());
      abonnesEtendus.clear();
    }, () => { chargement = null; });
  }
  return chargement;
}
if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
  window.requestIdleCallback(() => chargerIconesEtendues(), { timeout: 8000 });
}
// Taille et ligne de base en em, par --em-ajuste (theme.css) : là où Safari
// iOS et iPadOS agrandissent le texte (data-texte-ajuste), ils appliquent
// l'échelle deux fois aux em, et l'icône dépasserait le texte de 38 % en
// « Très grand ».
const EM_ICONE = "calc(1.15em * var(--em-ajuste, 1))";
const EM_LIGNE = "calc(-0.2em * var(--em-ajuste, 1))";
export function IconeTexte({ nom, couleur, titre }) {
  const base = ICONES[nom];
  const [, rafraichir] = useState(0);
  useEffect(() => {
    if (base || tracesEtendus) return undefined;
    const f = () => rafraichir((n) => n + 1);
    abonnesEtendus.add(f);
    chargerIconesEtendues();
    return () => { abonnesEtendus.delete(f); };
  }, [base]);
  const traces = base || (tracesEtendus && tracesEtendus[nom]);
  // La case d'attente garde le nom accessible : un bouton à icône seule ne
  // doit jamais être muet, même avant l'arrivée du morceau.
  if (!traces) {
    return <span {...(titre ? { role: "img", "aria-label": titre } : { "aria-hidden": "true" })}
      style={{ display: "inline-block", width: EM_ICONE, height: EM_ICONE, verticalAlign: EM_LIGNE }} />;
  }
  return <Icone name={nom} traces={traces} size="1.15em" strokeWidth={2} titre={titre}
    style={{ verticalAlign: EM_LIGNE, width: EM_ICONE, height: EM_ICONE, color: couleur }} />;
}

const NAVY = NAV_COLORS.pilotage;

export function Tuile({ couleur = NAVY, taille = 28, icone, variante = "pleine", titre, style, className, children, ...attributs }) {
  const hex = /^#[0-9a-f]{6}$/i.test(couleur || "");
  const navy = String(couleur).toUpperCase() === NAVY.toUpperCase();
  let fond, encre;
  if (variante === "pleine") {
    // Navy en thème sombre : une tuile navy se fondrait dans le fond navy de
    // l'app — theme.css porte la variante (--tuile-navy, --tuile-navy-glyphe),
    // déclarée dans les deux thèmes.
    fond = navy ? "var(--tuile-navy)" : couleur;
    encre = navy ? "var(--tuile-navy-glyphe)" : encreSur(couleur);
  } else {
    // Lavis navy : #003F59 à 14 % sur la carte sombre (#0a3446) ne s'en
    // distingue pas (≈ 1,03:1, même teinte). On lave à l'ENCRE du thème
    // (--tx-navy : #7FB3D5 en sombre ≈ 1,3:1, comme les autres teintes ; en
    // clair #003F59, rendu identique à l'ancien #003F5924).
    fond = variante === "lavis"
      ? (navy ? `color-mix(in srgb, ${navTexte(couleur)} 14%, transparent)`
        : hex ? alpha(couleur, .141) : `color-mix(in srgb, ${couleur} 14%, transparent)`)
      : "transparent";
    encre = navTexte(couleur);
  }
  const glyphe = Math.max(12, Math.round(taille * 0.6));
  return (
    <span {...attributs} className={className} role={titre ? "img" : undefined} aria-label={titre || undefined}
      aria-hidden={titre ? undefined : "true"}
      style={{ width: taille, height: taille, minWidth: taille, flexShrink: 0, boxSizing: "border-box",
        borderRadius: "var(--radius-tile, 22%)", display: "inline-grid", placeItems: "center",
        background: fond, color: encre, lineHeight: 0, ...style }}>
      {icone
        ? <IconeOuTexte icone={icone} size={glyphe}
            style={estIcone(icone) ? undefined : { fontSize: taillePx(Math.round(taille * 0.5)) }} />
        : children}
    </span>
  );
}
